import { Plant, RotationGroup } from '../types';

// Classic 4-bed rotation extended — what grows well after each group
const GOOD_FOLLOWS: Record<RotationGroup, RotationGroup[]> = {
  legumes:    ['brassicas', 'solanaceae'],   // legumes fix nitrogen — heavy feeders love following them
  brassicas:  ['roots', 'alliums'],          // brassicas deplete nitrogen — roots/alliums need less
  roots:      ['legumes', 'cucurbits'],      // roots loosen soil — legumes and cucurbits follow well
  alliums:    ['solanaceae', 'cucurbits'],   // alliums deter pests — good before fruiting crops
  solanaceae: ['legumes', 'roots'],          // solanaceae are heavy feeders — follow with restorers
  cucurbits:  ['legumes', 'brassicas'],      // cucurbits are also heavy feeders
  other:      [],
};

export type RotationStatus = 'good' | 'bad' | 'neutral';

export function getRotationStatus(candidate: Plant, previousPlant: Plant): RotationStatus {
  if (candidate.rotationGroup === previousPlant.rotationGroup) return 'bad';
  const good = GOOD_FOLLOWS[previousPlant.rotationGroup];
  if (good.includes(candidate.rotationGroup)) return 'good';
  return 'neutral';
}

export function getRotationReason(candidate: Plant, previousPlant: Plant): string {
  if (candidate.rotationGroup === previousPlant.rotationGroup) {
    return `${previousPlant.name} was here last season — planting the same family again can deplete the soil and let pests build up.`;
  }
  if (getRotationStatus(candidate, previousPlant) === 'good') {
    if (candidate.rotationGroup === 'legumes') {
      return `${candidate.name} fix nitrogen back into the soil that ${previousPlant.name} depleted — an ideal rotation.`;
    }
    return `${candidate.name} follows ${previousPlant.name} well — rotating to a different family keeps the soil balanced and breaks pest cycles.`;
  }
  return '';
}

export function getRotationSuggestion(previousPlant: Plant): string {
  const good = GOOD_FOLLOWS[previousPlant.rotationGroup];
  if (good.length === 0) return '';
  const labels: Record<RotationGroup, string> = {
    legumes:    'legumes (peas, beans)',
    brassicas:  'brassicas (cabbage, radish)',
    roots:      'roots (carrot, parsnip)',
    alliums:    'alliums (onion, garlic)',
    solanaceae: 'tomatoes or peppers',
    cucurbits:  'cucurbits (courgette, cucumber)',
    other:      'other crops',
  };
  const suggestions = good.map(g => labels[g]).join(' or ');
  return `After ${previousPlant.name}, consider ${suggestions} for good rotation.`;
}
